import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { AdminMenuCounts as ServerMenuCounts, AdminOverview } from '@nest-recruit/shared';
import { ApiError, apiGet, apiPost } from '../../../lib/api-client';
import { INVITES_KEY, USERS_KEY } from './users-api';

// ADMIN-21 Overview and the Admin menu's red counts (Admin redesign 2026-11-02, brief items 3 and 4). Both are
// counted live by the server on every request and never stored (non-negotiable #10).

export const ADMIN_OVERVIEW_KEY = ['admin', 'overview'] as const;

/** How long one Overview answer is reused before a visit asks again (review R13). */
export const OVERVIEW_STALE_MS = 30_000;

/**
 * Under the invites' key on purpose: everything that changes a pending invite (invite, invite several, resend,
 * cancel, bulk resend) already refreshes `['users']`, so the People count follows without each of those knowing
 * the menu exists. A realtime push on `['users']` and the socket's reconnect refetch reach it the same way.
 */
export const ADMIN_MENU_COUNTS_KEY = [...INVITES_KEY, 'admin-menu-counts'] as const;

/** Admin only: anyone else gets 403, which the page shows as a refusal. */
export function useAdminOverview(enabled: boolean): UseQueryResult<AdminOverview, ApiError> {
  const queryClient = useQueryClient();
  return useQuery<AdminOverview, ApiError>({
    queryKey: ADMIN_OVERVIEW_KEY,
    queryFn: async () => {
      const overview = await apiGet<AdminOverview>('/admin/overview');
      // The board and the menu beside it must never disagree about the same two numbers.
      queryClient.setQueryData<ServerMenuCounts>(ADMIN_MENU_COUNTS_KEY, overview.menuCounts);
      return overview;
    },
    // A health board is only worth opening if it is now, but it costs the server a few dozen counts (review R13):
    // a visit within half a minute of the last reuses it, and anything changed here invalidates it at once. The last
    // answer stays on screen while a new one loads (one fixed key, so the cache itself keeps it: never blank data).
    staleTime: OVERVIEW_STALE_MS,
    enabled,
    // A refusal is an answer, not a blip.
    retry: false,
  });
}

/** Any signed-in person may ask; a count is null where they may not open its section. */
export function useServerMenuCounts(enabled: boolean): UseQueryResult<ServerMenuCounts, ApiError> {
  return useQuery<ServerMenuCounts, ApiError>({
    queryKey: ADMIN_MENU_COUNTS_KEY,
    queryFn: () => apiGet<ServerMenuCounts>('/admin/menu-counts'),
    enabled,
  });
}

interface SendAgainSnapshot {
  readonly overview: AdminOverview | undefined;
  readonly menuCounts: ServerMenuCounts | undefined;
}

/**
 * "Send again" from the Overview: a new link goes out and the old one stops working, so the invite is no longer
 * waiting. Review R23: its line leaves "Needs your attention" at once and the count beside it and in the menu drop by
 * one together; a refusal puts both back. There is no Undo, because the email has already gone (owner, 2026-11-02).
 */
export function useSendInviteAgain() {
  const queryClient = useQueryClient();
  return useMutation<{ inviteUrl: string }, ApiError, string, SendAgainSnapshot>({
    mutationFn: (inviteId) => apiPost<{ inviteUrl: string }>(`/users/invites/${inviteId}/resend`),
    onMutate: async (inviteId) => {
      await queryClient.cancelQueries({ queryKey: ADMIN_OVERVIEW_KEY });
      const snapshot: SendAgainSnapshot = {
        overview: queryClient.getQueryData<AdminOverview>(ADMIN_OVERVIEW_KEY),
        menuCounts: queryClient.getQueryData<ServerMenuCounts>(ADMIN_MENU_COUNTS_KEY),
      };
      if (snapshot.overview) queryClient.setQueryData<AdminOverview>(ADMIN_OVERVIEW_KEY, withoutInvite(snapshot.overview, inviteId));
      if (snapshot.menuCounts && snapshot.menuCounts.pendingInvites !== null) {
        queryClient.setQueryData<ServerMenuCounts>(ADMIN_MENU_COUNTS_KEY, {
          ...snapshot.menuCounts,
          pendingInvites: Math.max(0, snapshot.menuCounts.pendingInvites - 1),
        });
      }
      return snapshot;
    },
    onError: (_error, _inviteId, snapshot) => {
      if (snapshot?.overview) queryClient.setQueryData(ADMIN_OVERVIEW_KEY, snapshot.overview);
      if (snapshot?.menuCounts) queryClient.setQueryData(ADMIN_MENU_COUNTS_KEY, snapshot.menuCounts);
    },
    onSettled: () =>
      Promise.all([queryClient.invalidateQueries({ queryKey: USERS_KEY }), queryClient.invalidateQueries({ queryKey: ADMIN_OVERVIEW_KEY })]),
  });
}

/** The Overview with one invite sent again: off the list, one fewer counted, and the line gone when none is left. */
export function withoutInvite(overview: AdminOverview, inviteId: string): AdminOverview {
  const line = overview.attention.find((item) => item.kind === 'invites_unaccepted');
  if (!line || line.kind !== 'invites_unaccepted' || !line.invites.some((invite) => invite.inviteId === inviteId)) return overview;
  const count = Math.max(0, line.count - 1);
  const attention =
    count === 0
      ? overview.attention.filter((item) => item !== line)
      : overview.attention.map((item) => (item === line ? { ...line, count, invites: line.invites.filter((invite) => invite.inviteId !== inviteId) } : item));
  const pendingInvites = overview.menuCounts.pendingInvites === null ? null : Math.max(0, overview.menuCounts.pendingInvites - 1);
  return {
    ...overview,
    attention,
    headline: { ...overview.headline, needsYou: attention.length },
    menuCounts: { ...overview.menuCounts, pendingInvites },
  };
}
